// Password generation backed by crypto.getRandomValues — Math.random() is
// seeded predictably enough that it has no place near a password.

const SETS = {
  lower: 'abcdefghijklmnopqrstuvwxyz',
  upper: 'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  digits: '0123456789',
  symbols: '!@#$%^&*()-_=+[]{};:,.?/~',
};

// Characters that are easy to misread when a password is typed off a screen.
const AMBIGUOUS = /[Il1O0oB8S5Z2]/g;

export const MIN_LENGTH = 4;
export const MAX_LENGTH = 64;

const randomBytes = (count) => {
  const buffer = new Uint32Array(count);
  const source = typeof window !== 'undefined' && window.crypto;
  if (!source || !source.getRandomValues) throw new Error('crypto-unavailable');
  source.getRandomValues(buffer);
  return buffer;
};

// Rejection sampling: taking `random % poolSize` would quietly favour the
// first characters of the pool whenever 2^32 is not a multiple of poolSize.
const randomIndex = (poolSize) => {
  const limit = Math.floor(0xffffffff / poolSize) * poolSize;
  for (;;) {
    const [value] = randomBytes(1);
    if (value < limit) return value % poolSize;
  }
};

export const buildPool = ({ lower, upper, digits, symbols, avoidAmbiguous }) => {
  const parts = [];
  if (lower) parts.push(SETS.lower);
  if (upper) parts.push(SETS.upper);
  if (digits) parts.push(SETS.digits);
  if (symbols) parts.push(SETS.symbols);

  return parts
    .map((set) => (avoidAmbiguous ? set.replace(AMBIGUOUS, '') : set))
    .filter((set) => set.length > 0);
};

/**
 * Generates a password containing at least one character from every selected
 * set, then shuffles so the guaranteed characters do not sit in a fixed order.
 */
export const generatePassword = (options) => {
  const length = Math.min(MAX_LENGTH, Math.max(MIN_LENGTH, Math.round(options.length || 16)));
  const pools = buildPool(options);
  if (pools.length === 0) return '';

  const all = pools.join('');
  const chars = pools.map((pool) => pool[randomIndex(pool.length)]).slice(0, length);
  while (chars.length < length) chars.push(all[randomIndex(all.length)]);

  // Fisher-Yates with the same uniform source.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = randomIndex(i + 1);
    [chars[i], chars[j]] = [chars[j], chars[i]];
  }

  return chars.join('');
};

/** Shannon entropy of the *generator*, which is what actually bounds guessing. */
export const passwordEntropy = (length, options) => {
  const poolSize = buildPool(options).join('').length;
  if (poolSize === 0 || length === 0) return 0;
  return Math.round(length * Math.log2(poolSize));
};

/** Buckets entropy into the four labels the UI shows. */
export const passwordStrength = (entropy) => {
  if (entropy < 40) return 'weak';
  if (entropy < 60) return 'fair';
  if (entropy < 90) return 'good';
  return 'strong';
};
